import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";

interface SignUpFormProps {
  onToggle: () => void;
}

const SignUpForm = ({ onToggle }: SignUpFormProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match", {
        description: "Please make sure your passwords match.",
      });
      return;
    }

    if (!agreeTerms) {
      toast.error("Terms required", {
        description: "Please agree to the terms and conditions.",
      });
      return;
    }

    setIsLoading(true);
    
    // Simulate sign up
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    toast.success(t("createAccount"), {
      description: t("createAccountSubtitle"),
    });
    
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name Field */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium text-brand-ink-900">
          {t("fullName")}
        </Label>
        <Input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="border-brand-line bg-brand-paper-50 text-brand-ink-900 hover:border-brand-ink-800/40 focus:border-brand-ink-800 focus:ring-brand-ink-800/20"
        />
      </div>

      {/* Email Field */}
      <div className="space-y-2">
        <Label htmlFor="signup-email" className="text-sm font-medium text-brand-ink-900">
          {t("emailAddress")}
        </Label>
        <Input
          id="signup-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="border-brand-line bg-brand-paper-50 text-brand-ink-900 hover:border-brand-ink-800/40 focus:border-brand-ink-800 focus:ring-brand-ink-800/20"
        />
      </div>

      {/* Password Field */}
      <div className="space-y-2">
        <Label htmlFor="signup-password" className="text-sm font-medium text-brand-ink-900">
          {t("password")}
        </Label>
        <Input
          id="signup-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="border-brand-line bg-brand-paper-50 text-brand-ink-900 hover:border-brand-ink-800/40 focus:border-brand-ink-800 focus:ring-brand-ink-800/20"
        />
      </div>

      {/* Confirm Password Field */}
      <div className="space-y-2">
        <Label htmlFor="confirm-password" className="text-sm font-medium text-brand-ink-900">
          {t("confirmPassword")}
        </Label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          className="border-brand-line bg-brand-paper-50 text-brand-ink-900 hover:border-brand-ink-800/40 focus:border-brand-ink-800 focus:ring-brand-ink-800/20"
        />
      </div>

      {/* Terms Agreement */}
      <div className="flex items-start gap-2">
        <Checkbox
          id="terms"
          checked={agreeTerms}
          onCheckedChange={(checked) => setAgreeTerms(checked as boolean)}
          className="mt-0.5 border-brand-line data-[state=checked]:border-brand-ink-800 data-[state=checked]:bg-brand-ink-800"
        />
        <Label
          htmlFor="terms"
          className="cursor-pointer text-sm leading-relaxed text-brand-ink-900/60"
        >
          {t("agreeTerms")}{" "}
          <span className="cursor-pointer border-b border-brand-gold-500 text-brand-ink-800 hover:text-brand-ink-600">
            {t("termsConditions")}
          </span>
        </Label>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full rounded-full bg-brand-ink-800 text-white hover:bg-brand-ink-900"
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            {t("creatingAccount")}
          </div>
        ) : (
          t("signUp")
        )}
      </Button>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-brand-line" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-brand-ink-900/60">
            {t("orContinueWith")}
          </span>
        </div>
      </div>

      {/* Social Buttons */}
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full border-brand-line bg-white text-brand-ink-900 transition-colors hover:border-brand-gold-500/40 hover:bg-brand-paper hover:text-brand-ink-900"
          onClick={() => toast.info(t("supabaseRequired"))}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {t("continueWithGoogle")}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full border-brand-line bg-white text-brand-ink-900 transition-colors hover:border-brand-gold-500/40 hover:bg-brand-paper hover:text-brand-ink-900"
          onClick={() => toast.info(t("supabaseRequired"))}
        >
          <svg className="w-5 h-5" viewBox="0 0 23 23">
            <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
            <path fill="#f35325" d="M1 1h10v10H1z"/>
            <path fill="#81bc06" d="M12 1h10v10H12z"/>
            <path fill="#05a6f0" d="M1 12h10v10H1z"/>
            <path fill="#ffba08" d="M12 12h10v10H12z"/>
          </svg>
          {t("continueWithMicrosoft")}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full border-brand-line bg-white text-brand-ink-900 transition-colors hover:border-brand-gold-500/40 hover:bg-brand-paper hover:text-brand-ink-900"
          onClick={() => toast.info(t("supabaseRequired"))}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          {t("continueWithApple")}
        </Button>
      </div>

      {/* Toggle to Sign In */}
      <p className="mt-6 text-center text-sm text-brand-ink-900/60">
        {t("haveAccount")}{" "}
        <button
          type="button"
          onClick={onToggle}
          className="font-medium text-brand-ink-800 transition-colors hover:text-brand-ink-600"
        >
          {t("signIn")}
        </button>
      </p>
    </form>
  );
};

export default SignUpForm;
